import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useIntl } from 'react-intl';
import { priceToString } from '../../../utils';
import styles from './AnimatedProductCard.module.scss';
import AnimationWrapper from '../AnimationWrapper';
import messages from './messages';

const AnimatedProductCard = ({
  title,
  description,
  price,
  name,
  id,
  discount = null,
  imageURL,
  badge = null,
  badgeIsAlwaysVisible = false,
  onChange,
  isSelected,
  badgeStyle = 'normal',
  hasVariants,
  displayDiscount,
}) => {
  const { formatMessage, formatNumber } = useIntl();

  const [prevBadgeText, setPrevBadgeText] = useState('');
  const [hasBadgeChanged, setHasBadgeChanged] = useState(false);
  const [hasPriceChanged, setHasPriceChanged] = useState(false);
  const [prevPriceText, setPrevPriceText] = useState('');
  const hasDiscount = discount?.centAmount > 0;

  useEffect(() => {
    if (badge !== null && prevBadgeText === '') {
      setPrevBadgeText(badge);
    } else if (badge !== null && (isSelected || badgeIsAlwaysVisible)) {
      setPrevBadgeText(badge);
    }
  }, [badge, isSelected, badgeIsAlwaysVisible, prevBadgeText]);

  useEffect(() => {
    setTimeout(() => {
      if (badge !== null && badge === prevBadgeText) {
        setHasBadgeChanged(true);
      } else {
        setHasBadgeChanged(false);
      }
    }, 10);

    setHasPriceChanged(true);
  }, [badge, prevBadgeText]);

  useEffect(() => {
    const beforePriceText = priceToString({
      price: hasDiscount
        ? {
            ...price,
            centAmount: price.centAmount + discount.centAmount,
          }
        : price,
      maximumFractionDigits: 0,
      formatNumber,
      formatMessage,
    });

    if (beforePriceText !== prevPriceText) {
      setPrevPriceText(beforePriceText);
      setHasPriceChanged(true);
    }
  }, [
    isSelected,
    hasVariants,
    prevPriceText,
    discount,
    formatMessage,
    formatNumber,
    price,
    hasDiscount,
  ]);

  useEffect(() => {
    if (hasPriceChanged) {
      setTimeout(() => {
        setHasPriceChanged(false);
      }, 10);
    }
  }, [hasPriceChanged]);

  return (
    <div
      className={classnames(styles['product-card-wrapper'], {
        [styles['is-selected']]: isSelected,
        [styles['not-selected']]: !isSelected,
      })}
      onClick={() => onChange(id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          onChange(id);
        }
      }}
      role="button"
      data-testid="product-card"
      tabIndex="0"
    >
      <div className={styles['product-card-overlay']}>
        <div className={styles['product-card-overlay-radio-button']}>
          <AnimationWrapper
            isShowing={isSelected}
            onHideClassName={styles.fadeOut}
            onShowClassName={styles.fadeIn}
          >
            <div
              className={styles['product-check-mark']}
              data-testid="check-mark"
            />
          </AnimationWrapper>
        </div>

        {prevBadgeText && (
          <div className={styles['product-card-overlay-badge']}>
            <AnimationWrapper
              isShowing={isSelected || badgeIsAlwaysVisible}
              onShowClassName={styles.slideBadgeFadeIn}
              onHideClassName={styles.slideBadgeFadeOut}
            >
              <div
                className={classnames({
                  [styles['product-badge']]: badgeStyle === 'normal',
                  [styles['alternative-badge']]: badgeStyle === 'alternative',
                  [styles['hidden-badge']]: badgeStyle === 'hidden',
                })}
                data-testid="product__card__badge"
              >
                <AnimationWrapper
                  isShowing={hasBadgeChanged}
                  onShowClassName={styles.fadeIn}
                  onHideClassName={styles.fadeOut}
                >
                  <span>{prevBadgeText}</span>
                </AnimationWrapper>
              </div>
            </AnimationWrapper>
          </div>
        )}
      </div>

      <div className={styles['product-image']}>
        <img src={imageURL} alt={name} />
      </div>
      <div className={styles['product-info']}>
        <div
          className={classnames(styles['product-title'], {
            [styles['product-title-with-badge']]: !!badge,
          })}
        >
          {title}
        </div>
        <div className={styles['product-description']}>{description}</div>
        <div className={styles['product-price-info']}>
          {hasVariants && !isSelected && (
            <div className={styles['product-price']}>
              <AnimationWrapper
                isShowing={!hasPriceChanged}
                onShowClassName={styles.fadeIn}
                onHideClassName={styles.fadeOut}
              >
                <span>{formatMessage(messages.variantPriceFrom)}</span>
              </AnimationWrapper>
            </div>
          )}
          <div
            className={classnames(styles['product-price-amount'], {
              [styles['product-price-line-through']]: displayDiscount,
            })}
          >
            <AnimationWrapper
              isShowing={!hasPriceChanged}
              onShowClassName={styles.fadeIn}
              onHideClassName={styles.fadeOut}
            >
              <span>{prevPriceText}</span>
            </AnimationWrapper>
          </div>

          <AnimationWrapper
            isShowing={!hasPriceChanged && hasDiscount && displayDiscount}
            onShowClassName={styles.fadeIn}
            onHideClassName={styles.fadeOut}
          >
            <div className={styles['product-discounted-price']}>
              {priceToString({
                price,
                maximumFractionDigits: 0,
                formatNumber,
                formatMessage,
              })}
            </div>
          </AnimationWrapper>
        </div>
      </div>
    </div>
  );
};
AnimatedProductCard.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  price: PropTypes.shape({
    type: PropTypes.string,
    currencyCode: PropTypes.string,
    centAmount: PropTypes.number.isRequired,
    fractionDigits: PropTypes.number.isRequired,
  }).isRequired,
  discount: PropTypes.shape({
    type: PropTypes.string,
    currencyCode: PropTypes.string,
    centAmount: PropTypes.number,
    fractionDigits: PropTypes.number,
  }),
  imageURL: PropTypes.string.isRequired,
  badge: PropTypes.string,
  badgeIsAlwaysVisible: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  isSelected: PropTypes.bool.isRequired,
  badgeStyle: PropTypes.oneOf(['normal', 'alternative', 'hidden']),
  hasVariants: PropTypes.bool.isRequired,
  displayDiscount: PropTypes.bool.isRequired,
};

export default AnimatedProductCard;
